<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted } from 'vue'
import { useAuditLogStore } from '@/stores/auditLog'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import AuditLogList from '@/components/audit/AuditLogList.vue'

const incidentId = inject<string>('incidentId')!
const auditLogStore = useAuditLogStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()

const canViewAudit = computed(() => permissionsStore.can('audit.view', opRole.value))

const FILTERS: { label: string; value: string | null }[] = [
  { label: 'All', value: null },
  { label: 'Team', value: 'team' },
  { label: 'Assignment', value: 'assignment' },
  { label: 'Personnel', value: 'personnel' },
  { label: 'Subject', value: 'subject' },
  { label: 'Form', value: 'form' },
  { label: 'Clue', value: 'clue' },
]

onMounted(() => {
  auditLogStore.subscribeToAuditLog(incidentId)
})

onUnmounted(() => {
  auditLogStore.teardown()
})
</script>

<template>
  <div class="h-full flex flex-col overflow-hidden">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
      <RouterLink
        :to="{ name: 'incident-settings', params: { id: incidentId } }"
        class="text-[10px] text-outline font-label uppercase tracking-widest hover:text-on-surface-variant transition-colors flex items-center gap-1"
      >
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        Back to Settings
      </RouterLink>
    </div>

    <div v-if="!canViewAudit" class="flex flex-col items-center justify-center py-24 gap-3">
      <span class="material-symbols-outlined text-[32px] text-outline-variant">lock</span>
      <p class="font-mono text-xs text-outline uppercase tracking-widest">Access restricted</p>
    </div>

    <template v-else>
      <!-- Counts -->
      <div class="px-6 pt-4 pb-2 shrink-0">
        <p class="font-mono text-xs text-outline">
          {{ auditLogStore.filteredEntries.length }}
          {{ auditLogStore.filteredEntries.length === 1 ? 'entry' : 'entries' }}
          <template v-if="auditLogStore.filteredEntries.length !== auditLogStore.entries.length">
            of {{ auditLogStore.entries.length }} total
          </template>
        </p>
      </div>

      <!-- Filter toolbar -->
      <div class="flex flex-wrap gap-2 px-6 pb-3 shrink-0">
        <button
          v-for="f in FILTERS"
          :key="String(f.value)"
          type="button"
          class="px-3 py-1 rounded text-xs font-mono uppercase tracking-wide border"
          :class="
            auditLogStore.filterEntityType === f.value
              ? 'bg-primary-container border-primary-container text-white'
              : 'bg-surface-container border-border text-outline hover:bg-surface-container-high hover:text-on-surface'
          "
          @click="f.value === null ? auditLogStore.clearFilter() : auditLogStore.setFilter(f.value)"
        >
          {{ f.label }}
        </button>
      </div>

      <!-- Log list -->
      <div class="overflow-y-auto px-6 pb-6 flex-1">
        <AuditLogList />
      </div>
    </template>
  </div>
</template>
